import { useTranslation } from "react-i18next";
import { useEffect, useRef } from "react";
import { ChevronLeft } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PerformanceReviewHeader } from "./review-header";
import { MetricCard, type MetricCardRef } from "./review-metric-card";
import { ResultSummary } from "./review-summary";
import {
  useGetEvaluationRequestDetailsByIdQuery,
  useGetPerformanceReviewByIdQuery,
  useUpdateEvaluationRequestDetailsByIdMutation,
} from "@/services/(company)/api.performance";
import type {
  IEvaluationRequestDetail,
  IEvaluationRequestDetailScore,
  IEvaluationRequestScorePayload,
} from "@/types/(company)/performance.interface";
import toast from "react-hot-toast";
import { startCase } from "lodash";
import { Button } from "@/components/ui/button";
import { PerformanceIndividualReviewSkeleton } from "./performance-review-skeleton";

/** Title per cycle type; anything unmapped falls back to the generic key. */
const CYCLE_TITLE_KEYS: Record<string, string> = {
  weekly: "company:performance_review.title_weekly",
  monthly: "company:performance_review.title_monthly",
  quarterly: "company:performance_review.title_quarterly",
  annually: "company:performance_review.title_annually",
};

export const PerformanceIndividualReview = () => {
  const { t } = useTranslation(["common", "company"]);
  const navigate = useNavigate();
  const { id: routeId, reviewId } = useParams();
  const id = reviewId ?? routeId;
  const [searchParams] = useSearchParams();
  const isReviewMode = searchParams.get("mode") === "review";
  const isCompany = searchParams.get("type") === "company";
  const hasRedirected = useRef(false);

  const metricRefs = useRef<Record<number, MetricCardRef | null>>({});

  // ─── Conditional Queries (skip pattern — hooks must always be called) ──────

  const { data: employeeData, isLoading: isEmployeeLoading } =
    useGetEvaluationRequestDetailsByIdQuery(
      { requestId: Number(id) },
      { skip: !id || isCompany },
    );

  const { data: companyData, isLoading: isCompanyLoading } =
    useGetPerformanceReviewByIdQuery(
      { id: String(id) },
      { skip: !id || !isCompany },
    );

  const isLoading = isCompany ? isCompanyLoading : isEmployeeLoading;
  const evaluationRequestData = isCompany ? companyData : employeeData;

  // ─── Mutation ─────────────────────────────────────────────────────────────

  const [updateEvaluationRequest, { isLoading: isSubmitting }] =
    useUpdateEvaluationRequestDetailsByIdMutation();

  const evaluationRequest = evaluationRequestData?.data as
    IEvaluationRequestDetail | undefined;
  const hasScores =
    evaluationRequest?.scores && evaluationRequest.scores.length > 0;

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    const refs = Object.values(metricRefs.current);

    const validationResults = await Promise.all(
      refs.map((ref) => ref?.validate() ?? Promise.resolve(true)),
    );

    const allValid = validationResults.every(Boolean);
    if (!allValid) {
      toast.error(t("company:performance_review.fill_all_required_fields"));
      return;
    }

    const body: IEvaluationRequestScorePayload[] = refs
      .map((ref) => ref?.getValues())
      .filter(Boolean)
      .map((v) => ({
        metricId: v!.metricId,
        score: v!.score,
        reason: v!.reason,
        areasOfImprovement: v!.areasOfImprovement,
      }));

    const toastId = toast.loading(
      t("company:performance_review.submitting_review"),
    );
    try {
      await updateEvaluationRequest({
        requestId: Number(id),
        payload: body,
      }).unwrap();
      toast.success(
        t("company:performance_review.review_submitted_successfully"),
        { id: toastId },
      );
      navigate(-1);
    } catch (e: any) {
      toast.error(
        e?.data?.message ??
          t("company:performance_review.failed_to_submit_review"),
        { id: toastId },
      );
    }
  };

  // ─── Guards ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isLoading && !evaluationRequest && !hasRedirected.current) {
      hasRedirected.current = true;
      toast.error(t("company:performance_review.no_performance_cycle_found"));
      navigate(-1);
    }
  }, [isLoading, evaluationRequest, navigate]);

  if (isLoading) return <PerformanceIndividualReviewSkeleton />;

  if (!evaluationRequest) return null;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <section className="flex flex-col gap-4 bg-white p-4 rounded-sm">
      {/* Top bar */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="h-9 w-9 flex items-center justify-center rounded-xs border border-border shrink-0"
        >
          <ChevronLeft size={18} className="text-primary rtl:rotate-180" />
        </button>
        <h1 className="text-lg font-semibold text-gray-800">
          {CYCLE_TITLE_KEYS[evaluationRequest.evaluation.cycleType]
            ? t(CYCLE_TITLE_KEYS[evaluationRequest.evaluation.cycleType])
            : t("company:performance_review.title_generic", {
                cycle: startCase(evaluationRequest.evaluation.cycleType),
              })}
        </h1>
      </div>

      <div
        className="flex flex-col border rounded-sm overflow-hidden "
        style={{ height: "calc(100vh - 180px)" }}
      >
        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* LEFT — only metrics scroll */}
          <div className="flex flex-col flex-1 min-h-0">
            {/* Fixed header */}
            <div className="shrink-0">
              <PerformanceReviewHeader
                employee={evaluationRequest.user}
                roleName={evaluationRequest.userRoleName}
                evaluatedBy={evaluationRequest.evaluator}
                isReviewMode={isReviewMode}
                team={evaluationRequest.evaluation.team}
              />
            </div>

            {/* Scrollable metrics only */}
            <div className="flex-1 overflow-y-auto min-h-0 p-5 flex flex-col gap-4 scrollbar-thin">
              {hasScores ? (
                evaluationRequest.scores.map(
                  (score: IEvaluationRequestDetailScore, index) => {
                    const metricScore = evaluationRequest.summary.metrics.find(
                      (metric) => metric.metricId === score.metricId,
                    );
                    return (
                      <MetricCard
                        key={score.id}
                        ref={(el) => {
                          metricRefs.current[score.metric.id] = el;
                        }}
                        score={score}
                        isReviewMode={isReviewMode}
                        metricScore={metricScore ?? null}
                        index={index + 1}
                      />
                    );
                  },
                )
              ) : (
                <div className="flex flex-1 items-center justify-center text-center text-gray-500">
                  <div>
                    <p className="text-sm font-medium">
                      {t("company:performance_review.no_scores_available")}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {t("company:performance_review.not_evaluated_yet")}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT — sticky, never scrolls */}
          {!isReviewMode && (
            <div className="w-80 shrink-0 border-s border-border">
              <ResultSummary
                overallPercent={evaluationRequest.summary.totalScore | 0}
                totalWeightedScore={evaluationRequest.summary.totalScore | 0}
                metrics={evaluationRequest.summary.metrics}
              />
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      {isReviewMode && (
        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="min-w-24 px-4 py-2 text-sm font-medium rounded-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("common:buttons.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="min-w-24 px-4 py-2 text-sm font-medium text-white bg-primary rounded-xs disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("company:leave_management.save")}
          </Button>
        </div>
      )}
    </section>
  );
};
